export interface CalculatorNumberField {
  label: string;
  unit: string;
  min: number;
  max: number;
  required?: boolean;
  positive?: boolean;
  integer?: boolean;
  decimalPlaces?: number;
}
const money = (label: string, unit = "₽"): CalculatorNumberField => ({ label, unit, min: 0, max: 1_000_000_000_000, ...(unit.startsWith("₽") ? { decimalPlaces: 2 } : {}) });
const years = (label: string): CalculatorNumberField => ({ label, unit: "лет", min: 0, max: 50 });
const pct = (label: string, growth = false): CalculatorNumberField => ({ label, unit: growth ? "%/год" : "%", min: growth ? -99 : 0, max: 100 });

// Shared browser/API contract. Form controls cannot invent their own ranges.
export const calculatorNumberFields = {
  price: { ...money("Стоимость квартиры"), required: true, positive: true },
  area: { ...money("Метраж", "м²"), required: true, positive: true },
  repairMonths: { label: "Срок ремонта", unit: "мес.", min: 0, max: 120, integer: true, required: true },
  investmentYears: { ...years("Горизонт"), required: true },
  ownershipYears: { ...years("До продажи"), required: true },
  valueIndex: pct("Рост стоимости", true),
  downPayment: money("Первоначальный взнос"),
  rate: pct("Ставка"),
  loanYears: years("Срок кредита"),
  manualPayment: money("Платёж вручную", "₽/мес."),
  rentRate: money("Ставка аренды", "₽/мес."),
  rentIndex: pct("Индексация", true),
  plannedRentYears: { ...years("Срок аренды"), required: true },
  rentTaxPct: { ...pct("Налог со сдачи"), required: true },
  repairCost: money("Ремонт под ключ"),
  furnitureCost: money("Мебель"),
  applianceCost: money("Бытовая техника"),
  insuranceLifeAnnual: money("Страхование жизни", "₽/год"),
  insuranceLifeYears: years("Срок страхования жизни"),
  insurancePropertyAnnual: money("Страхование имущества", "₽/год"),
  insurancePropertyYears: years("Срок страхования имущества"),
  appraisalCost: money("Оценка имущества"),
  utilities: money("Коммунальные без счётчиков", "₽/мес."),
  utilitiesGrowthPct: pct("Рост коммунальных услуг", true),
  managementPct: pct("Управляющая компания"),
  propertyTaxAnnual: money("Налог на имущество", "₽/год"),
  realtorPct: pct("Услуги риелтора при продаже"),
  saleTaxPct: pct("Налог с продажи"),
  minOwnYears: money("Владение без налога", "лет"),
  bookingCost: money("Бронь отдельно от ПВ"),
  registrationCost: money("Расходы по оформлению"),
  acceptanceCost: money("Приёмка квартиры"),
  dutyCost: money("Госпошлина"),
} satisfies Record<string, CalculatorNumberField>;
export type CalculatorNumberName = keyof typeof calculatorNumberFields;
export const expenseAmountField = money("Сумма расхода");
const compatibilityFields: Record<string, CalculatorNumberField> = {
  insuranceAnnual: money("Страхование"), insuranceTerm: years("Срок страхования"),
  extraPurchaseAmount: expenseAmountField, extraBeforeRentAmount: expenseAmountField, extraAfterRentAmount: expenseAmountField
};

export function numberFieldRule(name: string): CalculatorNumberField | undefined {
  return calculatorNumberFields[name as CalculatorNumberName] || compatibilityFields[name] || (/^extra(Purchase|BeforeRent|AfterRent)Amount-\d+$/.test(name) ? expenseAmountField : undefined);
}

export function numberFieldError(value: unknown, rule: CalculatorNumberField): string | null {
  if (value === undefined || value === "") {
    return rule.required ? (rule.label === "Налог со сдачи" ? "Укажите ставку налога со сдачи" : `Заполните поле «${rule.label}»`) : null;
  }
  if ((typeof value !== "string" && typeof value !== "number") ||
      (typeof value === "string" && !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(value.trim())) || !Number.isFinite(Number(value))) {
    return `«${rule.label}»: допустимы только конечные числа в десятичной записи`;
  }
  const number = Number(value);
  if (rule.positive && number <= 0) return `«${rule.label}»: значение должно быть больше нуля`;
  if (number < rule.min) return rule.min === 0 ? `«${rule.label}»: значения не могут быть отрицательными` : `«${rule.label}»: значение от ${rule.min}`;
  if (number > rule.max) return rule.max === 50 ? `«${rule.label}»: срок не должен превышать 50 лет` : `«${rule.label}»: значение до ${rule.max}`;
  if (rule.decimalPlaces !== undefined && Number(number.toFixed(rule.decimalPlaces)) !== number) return `«${rule.label}»: введите рубли и копейки, не более двух знаков после запятой`;
  if (rule.integer && !Number.isInteger(number)) return `«${rule.label}»: введите целое число месяцев`;
  return null;
}

export function calculatorNumberIssues(body: Record<string, unknown>) {
  return Object.entries({ ...calculatorNumberFields, ...compatibilityFields }).flatMap(([field, rule]) => {
    const message = numberFieldError(body[field], rule);
    return message ? [{ field, message }] : [];
  });
}
